import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { cn } from '@/shared/lib/cn';

import { abrirNaCamadaDeCima, posicionarJunto, temPopover } from './flutuante';
import { Icon, type IconName } from './Icon';
import { Spinner } from './Spinner';
import { tiposDeAcao, type TipoDeAcao } from './tiposDeAcao';
import { actionTone, type ToneName } from './tones';

/**
 * Acao de linha com icone colorido: o botao (ou o link) so com o icone, a dica com o
 * nome da acao ao passar o mouse e no foco do teclado, e o nome acessivel com o mesmo
 * texto que o botao tinha.
 *
 * Serve as acoes das linhas de tabela e de lista e aos grupos compactos dos cartoes
 * (Visualizar, Imprimir, Bloquear, Excluir). A acao principal da tela e do dialogo
 * (Salvar, Nova operacao, Gerar minuta) continua com texto, no `Button`.
 *
 * O icone e a cor vem do tipo da acao (`tiposDeAcao.ts`), e a cor nunca e o unico
 * sinal: a dica e o nome acessivel dizem a acao. O alvo de clique tem 32 px.
 */

type QualIcone =
  | {
      /** O tipo da acao, que da o icone e a cor (`tiposDeAcao.ts`). */
      acao: TipoDeAcao;
      /** O icone, quando a acao pede outro que nao o do tipo. */
      icone?: IconName | undefined;
    }
  | { acao?: undefined; icone: IconName };

type Aparencia = QualIcone & {
  /** A cor, quando a acao pede outra que nao a do tipo. */
  tom?: ToneName | undefined;
  /**
   * O nome da acao, o mesmo texto que o botao tinha: e a dica e o nome que o leitor de
   * tela le, a menos que `aria-label` diga mais (como o numero da linha).
   */
  rotulo: string;
  /**
   * Texto curto ao lado do icone, para a coluna em que so o icone nao fica claro.
   * `true` repete o rotulo. O texto precisa estar contido no nome acessivel.
   */
  texto?: string | boolean | undefined;
  className?: string | undefined;
};

export type AcaoComIconeProps = Aparencia &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'title' | 'className'> & {
    loading?: boolean | undefined;
    /**
     * Por que a acao esta desligada (ou travada): aparece na dica, embaixo do nome, e o
     * leitor de tela le como descricao do botao.
     */
    motivo?: string | undefined;
  };

export type LinkComIconeProps = Aparencia &
  Omit<LinkProps, 'children' | 'title' | 'className'>;

const TAMANHO_DO_ICONE = 17;

/** Word e PDF levam a sigla no selo: um passo maior, para a sigla se ler no chip. */
const ICONES_COM_SIGLA: ReadonlySet<IconName> = new Set(['word', 'pdf']);

function tamanhoDo(icone: IconName): number {
  return ICONES_COM_SIGLA.has(icone) ? 21 : TAMANHO_DO_ICONE;
}

function aparencia(props: {
  acao?: TipoDeAcao | undefined;
  icone?: IconName | undefined;
  tom?: ToneName | undefined;
}) {
  const doTipo = props.acao ? tiposDeAcao[props.acao] : undefined;
  return {
    icone: props.icone ?? doTipo?.icone ?? 'eye',
    tom: props.tom ?? doTipo?.tom ?? 'brand',
  };
}

function textoVisivel(
  texto: string | boolean | undefined,
  rotulo: string,
): string | null {
  if (texto === true) return rotulo;
  if (typeof texto === 'string' && texto !== '') return texto;
  return null;
}

/** O chip do botao: o degrade suave do tom, o contorno fino e a sombra curta. */
function classeDaAcao(tom: ToneName, comTexto: boolean, className?: string): string {
  return cn(
    'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg',
    'bg-linear-to-br ring-1 ring-inset shadow-(--shadow-xs) select-none',
    'transition-[box-shadow,color,transform,opacity] duration-(--duration-fast) ease-(--ease-out-soft)',
    'hover:-translate-y-px hover:shadow-(--shadow-sm) active:translate-y-0',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
    // O botao desligado nao recebe o mouse: quem recebe e a caixa da dica, que mostra
    // o motivo.
    'disabled:pointer-events-none disabled:opacity-45 disabled:shadow-none',
    comTexto ? 'px-2.5 text-xs font-medium whitespace-nowrap' : 'w-8',
    actionTone[tom],
    className,
  );
}

export function AcaoComIcone({
  acao,
  icone,
  tom,
  rotulo,
  texto,
  className,
  loading = false,
  motivo,
  disabled,
  type = 'button',
  'aria-label': nomeAcessivel,
  'aria-describedby': descritoPor,
  ...rest
}: AcaoComIconeProps) {
  const visual = aparencia({ acao, icone, tom });
  const escrito = textoVisivel(texto, rotulo);
  const motivoId = `${useId()}-motivo`;
  const desligado = disabled === true || loading;
  const descricao =
    [descritoPor, motivo ? motivoId : null].filter(Boolean).join(' ') || undefined;

  return (
    <ComDica rotulo={rotulo} motivo={motivo} desligado={desligado}>
      <button
        type={type}
        aria-label={nomeAcessivel ?? rotulo}
        aria-describedby={descricao}
        aria-busy={loading || undefined}
        disabled={desligado}
        className={classeDaAcao(visual.tom, escrito !== null, className)}
        {...rest}
      >
        {loading ? (
          <Spinner size={15} />
        ) : (
          <Icon name={visual.icone} size={tamanhoDo(visual.icone)} />
        )}
        {escrito}
      </button>
      {motivo ? (
        <span id={motivoId} className="sr-only">
          {motivo}
        </span>
      ) : null}
    </ComDica>
  );
}

/** A mesma acao, quando ela leva a outra tela (Ver a operacao, Analisar). */
export function LinkComIcone({
  acao,
  icone,
  tom,
  rotulo,
  texto,
  className,
  'aria-label': nomeAcessivel,
  ...rest
}: LinkComIconeProps) {
  const visual = aparencia({ acao, icone, tom });
  const escrito = textoVisivel(texto, rotulo);
  return (
    <ComDica rotulo={rotulo}>
      <Link
        aria-label={nomeAcessivel ?? rotulo}
        className={classeDaAcao(visual.tom, escrito !== null, className)}
        {...rest}
      >
        <Icon name={visual.icone} size={tamanhoDo(visual.icone)} />
        {escrito}
      </Link>
    </ComDica>
  );
}

/**
 * A dica do botao de icone, no estilo do balao do (i) (`FieldLabel.tsx`).
 *
 * Fica por cima da tela (`flutuante.ts`), centrada e acima do botao, e por isso nao
 * aumenta a area rolavel da tabela nem e cortada pela borda dela. Aparece ao passar o
 * mouse, depois de uma pausa curta, e no foco do teclado, sem a pausa. O clique fecha
 * a dica (quem clicou ja sabe o que o botao faz), e Esc, rolar ou redimensionar a
 * janela tambem fecham.
 *
 * O texto da dica repete o nome acessivel e o motivo, que o leitor de tela ja le pelo
 * proprio botao: por isso a dica fica fora da arvore de acessibilidade.
 */
function ComDica({
  rotulo,
  motivo,
  desligado = false,
  children,
}: {
  rotulo: string;
  motivo?: string | undefined;
  desligado?: boolean | undefined;
  children: ReactNode;
}) {
  const [aberta, setAberta] = useState<'mouse' | 'teclado' | null>(null);
  const caixa = useRef<HTMLSpanElement>(null);
  const balao = useRef<HTMLSpanElement>(null);
  // O foco que vem do clique nao abre a dica: so o foco do teclado.
  const peloPonteiro = useRef(false);

  useEffect(() => {
    if (!aberta) return;
    const fechar = () => setAberta(null);
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberta(null);
    };
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    document.addEventListener('keydown', tecla);
    return () => {
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberta]);

  // Antes de pintar: sobe a dica para a camada de cima e a coloca junto do botao.
  useLayoutEffect(() => {
    const elemento = balao.current;
    const ancora = caixa.current;
    if (!aberta || !elemento || !ancora) return;
    abrirNaCamadaDeCima(elemento);
    posicionarJunto(elemento, ancora, 'centro', 'acima');
  }, [aberta]);

  return (
    <span
      ref={caixa}
      className={cn('inline-flex shrink-0', desligado && 'cursor-not-allowed')}
      onMouseEnter={() => setAberta((atual) => atual ?? 'mouse')}
      onMouseLeave={() => setAberta((atual) => (atual === 'mouse' ? null : atual))}
      onPointerDown={() => {
        peloPonteiro.current = true;
        setAberta(null);
      }}
      onFocus={() => {
        if (!peloPonteiro.current) setAberta('teclado');
      }}
      onBlur={() => {
        peloPonteiro.current = false;
        setAberta(null);
      }}
    >
      {children}
      {aberta ? (
        <span
          ref={balao}
          aria-hidden="true"
          data-dica=""
          popover={temPopover ? 'manual' : undefined}
          className={cn(
            // O popover do navegador vem centrado na tela, com margem automatica: a
            // posicao e a do botao, escrita por `posicionarJunto`.
            'pointer-events-none fixed inset-auto z-40 m-0 block w-max',
            'max-w-[min(18rem,calc(100vw-1rem))] rounded-md border border-line bg-surface',
            'px-2 py-1 text-left text-xs leading-snug font-normal tracking-normal',
            'whitespace-normal text-ink-700 normal-case shadow-(--shadow-lg)',
            aberta === 'mouse' ? 'animate-dica' : 'animate-dica-ja',
          )}
        >
          <span className="block font-medium text-ink-900">{rotulo}</span>
          {motivo ? <span className="mt-0.5 block text-ink-600">{motivo}</span> : null}
        </span>
      ) : null}
    </span>
  );
}
